import React, { createContext, useContext, useState, ReactNode } from 'react';
import { FileStatus } from '../../../../hooks/useProjectSync';

interface FileStatusContextType {
  fileStatuses: FileStatus[];
  setFileStatuses: (statuses: FileStatus[]) => void;
  updateFileStatuses: (statuses: FileStatus[] | null) => void;
}

const FileStatusContext = createContext<FileStatusContextType | undefined>(
  undefined
);

export const useFileStatus = () => {
  const context = useContext(FileStatusContext);
  if (!context) {
    throw new Error('useFileStatus must be used within a FileStatusProvider');
  }
  return context;
};

interface FileStatusProviderProps {
  children: ReactNode;
}

export const FileStatusProvider: React.FC<FileStatusProviderProps> = ({
  children,
}) => {
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);

  const updateFileStatuses = React.useCallback(
    (statuses: FileStatus[] | null) => {
      setFileStatuses((prevStatuses) => {
        // Only update if the statuses actually changed to prevent infinite loops
        if (!statuses) {
          return prevStatuses.length === 0 ? prevStatuses : [];
        }

        // Check if statuses are different
        if (prevStatuses.length !== statuses.length) {
          return statuses;
        }

        const hasChanged = statuses.some((status, index) => {
          const prev = prevStatuses[index];
          return (
            !prev ||
            prev.fileName !== status.fileName ||
            prev.status !== status.status ||
            prev.isLocal !== status.isLocal
          );
        });

        return hasChanged ? statuses : prevStatuses;
      });
    },
    []
  );

  const value: FileStatusContextType = {
    fileStatuses,
    setFileStatuses,
    updateFileStatuses,
  };

  return (
    <FileStatusContext.Provider value={value}>
      {children}
    </FileStatusContext.Provider>
  );
};
